import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Search, ShieldAlert, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Column, DataTable } from "@/components/ui/data-table";
import {
  TENANT_STATUS_ACTIVE,
  TENANT_STATUS_SUSPENDED,
  TenantStorageFilters,
  TenantStorageSortBy,
  TenantStorageUsageDto,
  normalizeTenantStatus,
} from "@/types/admin-storage";
import { useAdminRegion } from "@/hooks/useAdminRegions";
import { useStorageRegion, useStorageTenants } from "@/hooks/useAdminStorage";
import { useDebounce } from "@/hooks/useDebounce";
import { formatBytes } from "@/utils/formatBytes";
import { cn } from "@/lib/utils";
import { StorageRegionHeader } from "./components/StorageRegionHeader";
import { StorageServersSection } from "./components/StorageServersSection";

type StatusFilter =
  | "all"
  | typeof TENANT_STATUS_ACTIVE
  | typeof TENANT_STATUS_SUSPENDED;

const STATUS_FILTERS: StatusFilter[] = [
  "all",
  TENANT_STATUS_ACTIVE,
  TENANT_STATUS_SUSPENDED,
];

const PAGE_SIZE = 25;

const STATUS_FILTER_LABEL_KEY: Record<StatusFilter, string> = {
  all: "storage.region.tenants.filters.all",
  [TENANT_STATUS_ACTIVE]: "storage.region.tenants.filters.active",
  [TENANT_STATUS_SUSPENDED]: "storage.region.tenants.filters.suspended",
};

const renderStatusBadge = (
  rawStatus: string,
  t: (key: string) => string,
): React.ReactNode => {
  const status = normalizeTenantStatus(rawStatus);
  if (status === TENANT_STATUS_ACTIVE) {
    return (
      <Badge variant="status_done">
        {t("storage.region.tenantStatus.Active")}
      </Badge>
    );
  }
  if (status === TENANT_STATUS_SUSPENDED) {
    return (
      <Badge variant="new_destructive">
        {t("storage.region.tenantStatus.Suspended")}
      </Badge>
    );
  }
  return <Badge variant="new_secondary">{rawStatus}</Badge>;
};

export const StorageRegionDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { regionCode = "" } = useParams<{ regionCode: string }>();

  const regionStatsQuery = useStorageRegion(regionCode);
  const regionMetaQuery = useAdminRegion(regionCode);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<TenantStorageSortBy>("totalBytes");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  // Reset to first page whenever filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, sortBy, sortOrder]);

  const tenantFilters = useMemo<TenantStorageFilters>(
    () => ({
      region: regionCode,
      search: debouncedSearch || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      sortBy,
      sortOrder,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [regionCode, debouncedSearch, statusFilter, sortBy, sortOrder, page],
  );

  const tenantsQuery = useStorageTenants(tenantFilters);

  const tenants = tenantsQuery.data?.data ?? [];
  const totalTenants = tenantsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalTenants / PAGE_SIZE));
  const isFiltering =
    debouncedSearch.trim().length > 0 || statusFilter !== "all";

  const handleSort = (field: string) => {
    const candidate = field as TenantStorageSortBy;
    if (
      candidate !== "totalBytes" &&
      candidate !== "fileCount" &&
      candidate !== "tenantName" &&
      candidate !== "region"
    ) {
      return;
    }
    if (sortBy === candidate) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(candidate);
      setSortOrder("desc");
    }
  };

  const computePercentOfRegion = (rowBytes: number): React.ReactNode => {
    const regionUsed = regionStatsQuery.data?.usedBytes;
    if (regionUsed === null || regionUsed === undefined) return "—";
    if (regionUsed === 0) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-xs">0%</span>
        </div>
      );
    }
    const percent = (rowBytes / regionUsed) * 100;
    return (
      <div className="flex items-center gap-2 min-w-[120px]">
        <div className="h-1.5 flex-1 rounded-full bg-secondary-badge overflow-hidden">
          <div
            className="h-full bg-royal-glow-3-light"
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>
        <span className="text-xs tabular-nums">
          {percent.toFixed(percent < 1 ? 2 : 1)}%
        </span>
      </div>
    );
  };

  const columns: Column<TenantStorageUsageDto>[] = [
    {
      key: "tenantName",
      header: t("storage.region.tenants.columns.name"),
      sortable: true,
    },
    {
      key: "tenantSlug",
      header: t("storage.region.tenants.columns.slug"),
      render: (_, row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.tenantSlug}
        </span>
      ),
    },
    {
      key: "status",
      header: t("storage.region.tenants.columns.status"),
      render: (_, row) => renderStatusBadge(row.status, t),
    },
    {
      key: "fileCount",
      header: t("storage.region.tenants.columns.files"),
      sortable: true,
      render: (_, row) => row.fileCount.toLocaleString(),
    },
    {
      key: "totalBytes",
      header: t("storage.region.tenants.columns.totalBytes"),
      sortable: true,
      render: (_, row) => formatBytes(row.totalBytes),
    },
    {
      key: "percentOfRegion",
      header: t("storage.region.tenants.columns.percentOfRegion"),
      render: (_, row) => computePercentOfRegion(row.totalBytes),
    },
  ];

  if (regionStatsQuery.isLoading) {
    return (
      <div className="p-12 text-sm text-muted-foreground">
        {t("storage.region.loading")}
      </div>
    );
  }

  if (regionStatsQuery.isError || !regionStatsQuery.data) {
    return (
      <div className="flex flex-col gap-3 p-12 max-w-md">
        <div className="flex items-center gap-2 text-destructive">
          <ShieldAlert className="size-5" />
          <span className="text-sm font-medium">
            {t("storage.region.loadError")}
          </span>
        </div>
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={() => regionStatsQuery.refetch()}
          className="w-fit"
        >
          {t("storage.list.retry")}
        </Button>
      </div>
    );
  }

  const regionName = regionMetaQuery.data?.name;

  return (
    <div className="flex flex-col gap-10 p-12 max-w-7xl">
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="new_ghost"
          size="sm"
          onClick={() => navigate("/storage")}
          className="w-fit"
        >
          <Undo2 className="size-4" />
          {t("storage.region.back")}
        </Button>
        <header className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-foreground">
              {regionName ?? regionCode}
            </h1>
            <Badge variant="new_outline" className="font-mono">
              {regionCode}
            </Badge>
            <Badge variant="new_outline">
              {t(`regions.storageType.${regionStatsQuery.data.storageType}`)}
            </Badge>
          </div>
        </header>
      </div>

      <StorageRegionHeader stats={regionStatsQuery.data} />
      <StorageServersSection servers={regionStatsQuery.data.servers} />

      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-foreground">
            {t("storage.region.tenants.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("storage.region.tenants.description")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[260px] max-w-md">
            <Input
              type="search"
              icon={<Search className="size-4 text-muted-foreground" />}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t("storage.region.tenants.search")}
            />
          </div>
          <div className="flex items-center gap-2" role="radiogroup">
            {STATUS_FILTERS.map((value) => (
              <Button
                key={value}
                type="button"
                role="radio"
                aria-checked={statusFilter === value}
                variant={statusFilter === value ? "new_secondary" : "new_ghost"}
                size="sm"
                onClick={() => setStatusFilter(value)}
                className={cn(
                  "min-w-[80px]",
                  statusFilter === value && "font-semibold",
                )}
              >
                {t(STATUS_FILTER_LABEL_KEY[value])}
              </Button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-base-border">
          <DataTable
            data={tenants}
            columns={columns}
            getRowKey={(row) => row.tenantId}
            loading={tenantsQuery.isLoading}
            emptyMessage={
              isFiltering
                ? t("storage.region.tenants.emptyFiltered")
                : t("storage.region.tenants.empty")
            }
            withPagination={totalPages > 1}
            pagination={
              totalPages > 1
                ? {
                    currentPage: page,
                    totalPages,
                    onPageChange: setPage,
                  }
                : undefined
            }
            sorting={{
              field: sortBy,
              direction: sortOrder,
              onSort: handleSort,
            }}
            onRowClick={(row) => navigate(`/storage/tenants/${row.tenantId}`)}
          />
        </div>
      </section>
    </div>
  );
};
